/**
 * Analytics Service
 * Central service for managing analytics tracking
 */

import { resolveEventDeliveries } from '../tracking/event-policy'
import type { AnalyticsProvider } from '../providers/base.provider'
import type { AnalyticsDeliveryPolicy, AnalyticsProviderKey } from '../tracking/event-policy'
import type { AnalyticsEventProperties } from '../types/properties'

interface AnalyticsContext {
  userId?: string
  contactId?: string
  locationId?: string
  groupSlug?: string
  groupId?: string
  pagePath?: string
  routeName?: string
  slugifiedRoute?: string
  email?: string
  appName?: string
  [key: string]: unknown
}

export interface AnalyticsIdentityScope {
  kind: 'platform'
  locationId: string
}

interface AnalyticsLogger {
  debug?: (message: string, context?: Record<string, unknown>) => void
  warn: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
}

interface QueuedEvent {
  event: string
  properties?: AnalyticsEventProperties
  timestamp: number
  deliveries: AnalyticsDeliveryPolicy[]
}

interface NavigationTransaction {
  token: NavigationContextToken
  queue: QueuedEvent[]
  overflowWarned: boolean
}

declare const navigationContextTokenBrand: unique symbol
export type NavigationContextToken = Readonly<{ [navigationContextTokenBrand]: true }>

const createNavigationContextToken = (): NavigationContextToken => Object.freeze({}) as NavigationContextToken

const MAX_PENDING_EVENTS = 100
/** CamelCase and snake_case aliases — GA flatten + toSnakeCase would otherwise let snake keys win. */
const RESERVED_CONTEXT_KEYS = new Set([
  'timestamp',
  'userId',
  'user_id',
  'contactId',
  'contact_id',
  'gokollabContactId',
  'gokollab_contact_id',
  'locationId',
  'location_id',
  'pagePath',
  'page_path',
  'routeName',
  'route_name',
  'slugifiedRoute',
  'slugified_route',
  'email',
  'appName',
  'app_name',
])

export interface AnalyticsRuntimePolicy {
  isAuthenticated: () => boolean
  allowsAnonymous?: (provider: AnalyticsProviderKey) => boolean
  /**
   * Automated traffic (crawler, prerender service, uptime monitor, browser automation). Checked
   * before session access so no destination — anonymous-capable ones included — receives bot
   * activity. Omitted means "not a bot", which keeps existing callers unchanged.
   */
  isBot?: () => boolean
}

/**
 * Removes reserved identity/tenant keys from caller-supplied properties, including inside the nested
 * `extra` bag that `v-track` bindings populate. Without the nested pass a declarative binding could
 * supply `locationId`/`location_id` (or contact/user aliases) and override the trusted context,
 * because the GA adapter spreads `extra` after the enriched top-level values and snake_cases keys.
 */
function stripReservedKeys(properties: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    if (RESERVED_CONTEXT_KEYS.has(key)) continue
    if (key === 'extra' && value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = stripReservedKeys(value as Record<string, unknown>)
      continue
    }
    result[key] = value
  }

  return result
}

export class AnalyticsService {
  private context: AnalyticsContext = {}
  private platformIdentity: { locationId: string; contactId: string } | null = null
  private providers: Map<string, AnalyticsProvider> = new Map()
  private navigationTransaction: NavigationTransaction | null = null
  private currentNavigationToken: NavigationContextToken | null = null
  private ordinaryTrackingBlocked = false
  private debug = false
  private logger: AnalyticsLogger | null = null
  private runtimePolicy: AnalyticsRuntimePolicy = { isAuthenticated: () => true }

  initialize(debug = false, logger?: AnalyticsLogger, runtimePolicy?: AnalyticsRuntimePolicy): void {
    this.debug = debug
    this.logger = logger ?? null
    this.runtimePolicy = runtimePolicy ?? { isAuthenticated: () => true }
    if (this.debug) this.logger?.debug?.('[Analytics] Service initialized')
  }

  registerProvider(name: string, provider: AnalyticsProvider): void {
    this.providers.set(name, provider)
    if (this.debug) this.logger?.debug?.('[Analytics] Provider registered', { provider: name })
  }

  getProvider(name: string): AnalyticsProvider | undefined {
    return this.providers.get(name)
  }

  setContext(context: Partial<AnalyticsContext>): void {
    this.context = { ...this.context, ...context }
    if (this.debug) this.logger?.debug?.('[Analytics] Context updated')
  }

  updateContext(key: string, value: unknown): void {
    this.context[key] = value
  }

  getContext(): AnalyticsContext {
    return { ...this.context }
  }

  clearContext(): void {
    this.context = {}
    this.platformIdentity = null
    this.navigationTransaction = null
    this.currentNavigationToken = null
    this.ordinaryTrackingBlocked = false
  }

  beginNavigationContext(): NavigationContextToken {
    const token = createNavigationContextToken()
    this.currentNavigationToken = token
    this.navigationTransaction = { token, queue: [], overflowWarned: false }
    this.ordinaryTrackingBlocked = false
    this.context = this.context.appName ? { appName: this.context.appName } : {}
    return token
  }

  commitNavigationContext(token: NavigationContextToken, context: Partial<AnalyticsContext>): boolean {
    if (this.currentNavigationToken !== token || this.navigationTransaction?.token !== token) return false
    const queuedEvents = this.navigationTransaction.queue
    this.navigationTransaction = null
    this.ordinaryTrackingBlocked = false
    this.context = { ...(this.context.appName ? { appName: this.context.appName } : {}), ...context }
    queuedEvents.forEach(item => this.dispatch(item.event, item.properties, item.timestamp, item.deliveries))
    return true
  }

  failNavigationContext(token: NavigationContextToken): boolean {
    if (this.currentNavigationToken !== token || this.navigationTransaction?.token !== token) return false
    this.navigationTransaction = null
    this.currentNavigationToken = null
    this.ordinaryTrackingBlocked = true
    return true
  }

  isNavigationContextCurrent(token: NavigationContextToken): boolean {
    return this.currentNavigationToken === token
  }

  teardownNavigationContext(): void {
    this.navigationTransaction = null
    this.currentNavigationToken = null
    this.ordinaryTrackingBlocked = true
  }

  private enrichProperties(
    properties: AnalyticsEventProperties | undefined,
    timestamp: number,
    contextMode: AnalyticsDeliveryPolicy['context']
  ): AnalyticsEventProperties {
    const callerProperties = stripReservedKeys(properties ?? {}) as AnalyticsEventProperties
    if (contextMode === 'none') return { ...callerProperties, timestamp }

    const scopedIdentity =
      !this.context.contactId && this.context.locationId && this.platformIdentity?.locationId === this.context.locationId
        ? { contactId: this.platformIdentity.contactId }
        : {}
    return {
      ...callerProperties,
      ...this.context,
      ...scopedIdentity,
      timestamp,
    }
  }

  track(event: string, properties?: AnalyticsEventProperties): void {
    const timestamp = Date.now()
    const deliveries = resolveEventDeliveries(event)
    const immediateDeliveries = deliveries.filter(item => item.delivery === 'immediate')
    const transactionalDeliveries = deliveries.filter(item => item.delivery === 'transactional')

    if (immediateDeliveries.length) this.dispatch(event, properties, timestamp, immediateDeliveries)
    if (!transactionalDeliveries.length) return

    if (this.navigationTransaction) {
      if (this.navigationTransaction.queue.length === MAX_PENDING_EVENTS) {
        this.navigationTransaction.queue.shift()
        if (!this.navigationTransaction.overflowWarned) {
          this.navigationTransaction.overflowWarned = true
          this.logger?.warn('[Analytics] Navigation event buffer overflow', {
            capacity: MAX_PENDING_EVENTS,
          })
        }
      }
      this.navigationTransaction.queue.push({ event, properties, timestamp, deliveries: transactionalDeliveries })
      return
    }
    if (this.ordinaryTrackingBlocked) return
    this.dispatch(event, properties, timestamp, transactionalDeliveries)
  }

  private dispatch(
    event: string,
    properties: AnalyticsEventProperties | undefined,
    timestamp: number,
    deliveries: AnalyticsDeliveryPolicy[]
  ): void {
    if (this.debug) this.logger?.debug?.('[Analytics] Event tracked', { event })

    deliveries.forEach(delivery => {
      const provider = this.providers.get(delivery.provider)
      if (!provider) return
      const eventProperties = this.enrichProperties(properties, timestamp, delivery.context)
      if (!this.canSend(delivery, eventProperties)) return
      try {
        provider.track(delivery.eventName, eventProperties)
      } catch (error) {
        this.logger?.error('[Analytics] Provider tracking failed', { provider: delivery.provider, error })
      }
    })
  }

  private canSend(delivery: AnalyticsDeliveryPolicy, properties: AnalyticsEventProperties): boolean {
    // Automated traffic is dropped ahead of every other check: bot page views are the one case where
    // an anonymous-capable destination must still receive nothing.
    if (this.runtimePolicy.isBot?.()) return false

    const isAuthenticated = this.runtimePolicy.isAuthenticated()
    const hasAccess =
      isAuthenticated || (delivery.access === 'anonymous' && Boolean(this.runtimePolicy.allowsAnonymous?.(delivery.provider)))
    if (!hasAccess) return false

    if (delivery.requiresIdentity === 'actor') return isAuthenticated
    if (delivery.requiresIdentity === 'route-contact') {
      return isAuthenticated && typeof properties.contactId === 'string' && Boolean(properties.contactId.trim())
    }
    return true
  }

  /** Records CRM contact identity for matching-location event envelopes. */
  identify(contactId: string, traits: Record<string, unknown> | undefined, scope: AnalyticsIdentityScope): void {
    const identityContext: Partial<AnalyticsContext> = {}
    if (typeof traits?.appName === 'string') identityContext.appName = traits.appName
    this.platformIdentity = { locationId: scope.locationId, contactId }
    this.setContext(identityContext)

    // Never log the contactId itself (CRM identity) — log only that identification occurred.
    if (this.debug) this.logger?.debug?.('[Analytics] Identify: user identified', { appName: traits?.appName })
  }

  /** Clears identity-bearing state on logout without discarding the active anonymous route. */
  clearIdentity(): void {
    this.platformIdentity = null
    delete this.context.userId
    delete this.context.contactId
    delete this.context.gokollabContactId
    delete this.context.email
  }
}

export const analyticsService = new AnalyticsService()
